import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { typeColor } from '../lib/format.js';

/**
 * Raid-Team gegen einen bestimmten Boss.
 *
 * Die Auswahl hängt am Boss: Gegen einen Eis-Boss zählt eine andere Attacke
 * als gegen einen Wasser-Boss, und zwar bei jedem Exemplar einzeln. Eine
 * feste Rangliste könnte das nicht abbilden.
 */
export default function RaidTeamPage() {
  const [bosse, setBosse] = useState(null);
  const [gewaehlt, setGewaehlt] = useState(null);
  const [team, setTeam] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.raidTeam().then((d) => setBosse(d.bosse)).catch((e) => setFehler(e.message));
  }, []);

  const waehlen = async (slug) => {
    setGewaehlt(slug);
    setTeam(null);
    setBusy(true);
    setFehler(null);
    try {
      setTeam(await api.raidTeam(slug));
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (fehler) return <div className="error">{fehler}</div>;
  if (!bosse) return <div className="skeleton" style={{ height: 240 }} />;

  return (
    <>
      <section className="hero">
        <span className="hero__tag">Raids</span>
        <h1>Dein Team gegen den Boss</h1>
        <p className="hero__sub">
          Aus deiner Sammlung, gerechnet gegen die Typen des Bosses — mit dem
          Mega, das der Gruppe am meisten bringt.
        </p>
      </section>

      <div className="filters">
        {bosse.map((b) => (
          <button
            key={b.slug}
            type="button"
            className={gewaehlt === b.slug ? 'chip chip--on' : 'chip'}
            onClick={() => waehlen(b.slug)}
          >
            {b.name}
          </button>
        ))}
      </div>

      {busy && <div className="skeleton" style={{ height: 180 }} />}

      {team && (
        <>
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="inline">
              <strong>{team.boss.name}</strong>
              {team.boss.typen.map((t) => (
                <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
              ))}
            </div>
          </div>

          {team.team.length === 0 ? (
            <div className="empty">
              {team.hinweis || 'Keine passenden Pokémon in deiner Sammlung.'}
            </div>
          ) : (
            <div className="stack">
              {team.team.map((k, i) => (
                <article className="team-zeile" key={k.id}>
                  <span className="team-zeile__nr">{i + 1}</span>

                  <div className="team-zeile__mitte">
                    <div className="spread">
                      <strong>{k.name}</strong>
                      <span className="text-faint">{k.wp} WP</span>
                    </div>

                    <div className="card__meta">
                      {k.schnell} → {k.lade}
                      {!k.gelernt && (
                        <span className="text-faint"> · beste mögliche Attacken</span>
                      )}
                    </div>

                    <div className="inline">
                      {k.typen.map((t) => (
                        <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
                      ))}
                      <span className="badge badge--soft">{k.ivs.join('/')}</span>
                      <span className="badge badge--soft">Lvl {k.level}</span>
                    </div>
                  </div>
                </article>
              ))}

              {team.mega && (
                <article className="team-zeile team-zeile--mega">
                  <span className="team-zeile__nr">M</span>
                  <div className="team-zeile__mitte">
                    <div className="spread">
                      <strong>{team.mega.name}</strong>
                      <span className="text-faint">{team.mega.wp} WP</span>
                    </div>
                    <div className="card__meta">
                      Verstärkt die anderen fünf — Attacken seines Typs um 30 %,
                      alle übrigen um 10 %.
                    </div>
                  </div>
                </article>
              )}

              {team.hinweis && <p className="card__meta">{team.hinweis}</p>}
            </div>
          )}
        </>
      )}
    </>
  );
}
